import { fieldKey, ID_KINDS, NUMBER_KINDS, DATE_KINDS } from "./schema.js";
/**
 * In-memory twin of `compileSql`. Runs in the sync layer (live query) and in
 * the browser (optimistic filtering). Must agree with the SQL compiler on
 * every issue; `test/equivalence.test.ts` enforces that.
 */
export function compilePredicate(q) {
    return pred(q.where);
}
function pred(p) {
    switch (p.kind) {
        case "true":
            return () => true;
        case "and": {
            const fs = p.items.map(pred);
            return (i) => fs.every((f) => f(i));
        }
        case "or": {
            const fs = p.items.map(pred);
            return (i) => fs.some((f) => f(i));
        }
        case "not": {
            const f = pred(p.pred);
            return (i) => !f(i);
        }
        case "field-in": {
            const key = fieldKey(p.field.id);
            const ids = new Set(p.ids);
            return (i) => (i.fields[key] ?? []).some((v) => ids.has(v));
        }
        case "field-empty": {
            const key = fieldKey(p.field.id);
            return (i) => (i.fields[key] ?? []).length === 0;
        }
        case "field-num": {
            const key = fieldKey(p.field.id);
            const { min, max } = p;
            return (i) => (i.fields[key] ?? []).some((v) => typeof v === "number" && (min === null || v >= min) && (max === null || v <= max));
        }
        case "field-date": {
            const key = fieldKey(p.field.id);
            const { from, to } = p;
            return (i) => (i.fields[key] ?? []).some((v) => typeof v === "number" && (from === null || v >= from) && (to === null || v <= to));
        }
        case "field-str": {
            const key = fieldKey(p.field.id);
            const needle = p.text.toLowerCase();
            return (i) => (i.fields[key] ?? []).some((v) => typeof v === "string" && v.toLowerCase().includes(needle));
        }
        case "project-in": {
            const ids = new Set(p.ids);
            return (i) => ids.has(i.projectId);
        }
        case "reporter-in": {
            const ids = new Set(p.ids);
            return (i) => ids.has(i.reporterId);
        }
        case "tag-in": {
            const ids = new Set(p.ids);
            return (i) => i.tags.some((t) => ids.has(t));
        }
        case "watcher-in": {
            const ids = new Set(p.ids);
            return (i) => (i.watchers ?? []).some((u) => ids.has(u));
        }
        case "builtin-date": {
            const { from, to } = p;
            const get = (i) => p.attr === "created" ? i.createdAt : p.attr === "updated" ? i.updatedAt : i.resolvedAt;
            return (i) => {
                const v = get(i);
                return v !== null && (from === null || v >= from) && (to === null || v <= to);
            };
        }
        case "number-range": {
            const { min, max } = p;
            return (i) => (min === null || i.number >= min) && (max === null || i.number <= max);
        }
        case "text": {
            const needle = p.text.toLowerCase();
            return (i) => i.summary.toLowerCase().includes(needle) || (i.descriptionText ?? "").toLowerCase().includes(needle);
        }
        case "resolved":
            return p.value ? (i) => i.resolvedAt !== null : (i) => i.resolvedAt === null;
    }
}
/**
 * Sort comparator matching the SQL `ORDER BY` from `compileSql`:
 * nulls last regardless of direction, then `id ASC` as the final tiebreak.
 * Strings compare by UTF-16 code unit, which equals Postgres `COLLATE "C"`
 * byte order for everything in the Basic Multilingual Plane.
 */
export function compileComparator(q, schema) {
    const keys = q.sort.map((s) => sortKey(s, schema));
    return (a, b) => {
        for (const k of keys) {
            const c = k(a, b);
            if (c !== 0)
                return c;
        }
        return a.id - b.id;
    };
}
function sortKey(s, schema) {
    const get = keyGetter(s, schema);
    const sign = s.dir === "asc" ? 1 : -1;
    return (a, b) => {
        const x = get(a);
        const y = get(b);
        if (x === null && y === null)
            return 0;
        if (x === null)
            return 1;
        if (y === null)
            return -1;
        if (typeof x === "string" && typeof y === "string")
            return sign * (x < y ? -1 : x > y ? 1 : 0);
        return sign * (x - y);
    };
}
function keyGetter(s, schema) {
    if (s.key.kind === "builtin") {
        switch (s.key.attr) {
            case "created":
                return (i) => i.createdAt;
            case "updated":
                return (i) => i.updatedAt;
            case "resolved":
                return (i) => i.resolvedAt;
            case "summary":
                return (i) => i.summary;
            case "number":
                return (i) => i.number;
            case "project":
                return (i) => i.projectId;
        }
    }
    const field = s.key.field;
    const key = fieldKey(field.id);
    if (ID_KINDS.has(field.kind) && field.bundleId !== undefined) {
        const ordinal = new Map(schema.elementsOf(field.bundleId).map((e) => [e.id, e.ordinal]));
        return (i) => {
            const v = i.fields[key]?.[0];
            if (v === undefined)
                return null;
            return ordinal.get(v) ?? null;
        };
    }
    if (NUMBER_KINDS.has(field.kind) || DATE_KINDS.has(field.kind) || ID_KINDS.has(field.kind)) {
        // users / groups sort by id; numbers and dates by value
        return (i) => {
            const v = i.fields[key]?.[0];
            return typeof v === "number" ? v : null;
        };
    }
    return (i) => {
        const v = i.fields[key]?.[0];
        return typeof v === "string" ? v : null;
    };
}
//# sourceMappingURL=compile-predicate.js.map